import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import TairLogo from './TairLogo';

const Header = () => {
  const { lang, toggle, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [lang]);

  const links = [
    { to: '/', label: t.nav.home, testid: 'nav-home' },
    { to: '/quienes-somos', label: t.nav.about, testid: 'nav-about' },
    { to: '/que-hacemos', label: t.nav.whatWeDo, testid: 'nav-what-we-do' },
    { to: '/contacto', label: t.nav.contact, testid: 'nav-contact' },
  ];

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'backdrop-blur-xl bg-[#070d18]/75 border-b border-white/[0.06]'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="header-logo-link" className="flex items-center">
            <TairLogo size="md" showSub />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {links.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                end={l.to === '/'}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    isActive ? 'text-amber-300' : 'text-white/70 hover:text-white'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="relative z-10 flex items-center gap-2">
                      <span className="font-mono text-[10px] text-white/35">0{i + 1}</span>
                      {l.label}
                    </span>
                    {isActive && (
                      <motion.span
                        layoutId="nav-pill"
                        className="absolute inset-0 rounded-full bg-white/[0.06] border border-white/10"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggle}
              data-testid="language-toggle"
              aria-label="Toggle language"
              className="hidden sm:inline-flex items-center gap-2 h-9 px-3 rounded-full border border-white/15 text-[10px] uppercase tracking-[0.22em] text-white/80 hover:text-white hover:border-amber-300/50 hover:bg-white/5 transition-all font-mono"
            >
              <Globe className="w-3.5 h-3.5" />
              <span className={lang === 'es' ? 'text-amber-300' : ''}>ES</span>
              <span className="text-white/30">/</span>
              <span className={lang === 'en' ? 'text-amber-300' : ''}>EN</span>
            </button>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/15 text-white/80 hover:text-white hover:bg-white/5"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden border-t border-white/5 bg-[#070d18]/95 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
              {links.map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === '/'}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${l.testid}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-base font-medium rounded-xl ${
                      isActive ? 'text-amber-300 bg-white/5' : 'text-white/80'
                    }`
                  }
                >
                  <span className="font-mono text-[10px] text-white/35">0{i + 1}</span>
                  {l.label}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={toggle}
                data-testid="mobile-language-toggle"
                className="mt-3 inline-flex items-center gap-2 self-start h-10 px-4 rounded-full border border-white/15 text-[10px] uppercase tracking-[0.22em] text-white/80 font-mono"
              >
                <Globe className="w-3.5 h-3.5" />
                <span className={lang === 'es' ? 'text-amber-300' : ''}>ES</span>
                <span className="text-white/30">/</span>
                <span className={lang === 'en' ? 'text-amber-300' : ''}>EN</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
